/**
 * Gradient-aware style utilities for the widget theme system.
 *
 * CSS `backgroundColor` does not support gradient values — only the
 * `background` shorthand does. These helpers detect whether a color value is
 * a solid hex/rgb or a CSS gradient string, and extract a solid fallback
 * color for surfaces that can't render gradients (borders, React Native).
 *
 * Parsing is deliberately explicit — small named steps instead of regexes —
 * so a bad input can be walked through in a debugger function by function.
 */
const GRADIENT_FUNCTIONS = ["linear-gradient", "radial-gradient", "conic-gradient", "repeating-linear-gradient", "repeating-radial-gradient", "repeating-conic-gradient"];
const COLOR_FUNCTIONS = ["rgba", "rgb", "hsla", "hsl"];
/** Detect whether a CSS color value is a gradient string. */
export function isGradient(color) {
    const normalized = color.trim().toLowerCase();
    return GRADIENT_FUNCTIONS.some((fn) => normalized.startsWith(`${fn}(`));
}
/**
 * Split a gradient's argument list on top-level commas, respecting nested
 * parentheses: "135deg, rgb(1, 2, 3), #fff" → ["135deg", "rgb(1, 2, 3)", "#fff"]
 */
function splitGradientArguments(gradient) {
    const open = gradient.indexOf("(");
    const close = gradient.lastIndexOf(")");
    if (open === -1 || close <= open)
        return [];
    const body = gradient.slice(open + 1, close);
    const args = [];
    let depth = 0;
    let current = "";
    for (const char of body) {
        if (char === "(")
            depth++;
        else if (char === ")")
            depth--;
        if (char === "," && depth === 0) {
            args.push(current.trim());
            current = "";
        }
        else {
            current += char;
        }
    }
    if (current.trim())
        args.push(current.trim());
    return args;
}
function isHexDigit(char) {
    return (char >= "0" && char <= "9") || (char >= "a" && char <= "f") || (char >= "A" && char <= "F");
}
/** "#f97316 40%" → "#f97316"; rejects malformed hex lengths. */
function takeHexColor(value) {
    let digits = "";
    for (const char of value.slice(1)) {
        if (!isHexDigit(char))
            break;
        digits += char;
    }
    const validLengths = [3, 4, 6, 8];
    return validLengths.includes(digits.length) ? `#${digits}` : null;
}
/** "rgb(1, 2, 3) 50%" → "rgb(1, 2, 3)" */
function takeColorFunction(value) {
    const normalized = value.toLowerCase();
    if (!COLOR_FUNCTIONS.some((fn) => normalized.startsWith(`${fn}(`)))
        return null;
    const close = value.indexOf(")");
    return close === -1 ? null : value.slice(0, close + 1);
}
/**
 * The color at the start of one gradient argument, or null for non-color
 * arguments like "135deg" or "circle at center".
 */
function leadingColor(argument) {
    const value = argument.trim();
    if (value.startsWith("#"))
        return takeHexColor(value);
    return takeColorFunction(value);
}
/** Extract all color stops from a gradient string, in order. */
export function extractColorsFromGradient(gradient) {
    if (!isGradient(gradient))
        return [];
    return splitGradientArguments(gradient)
        .map(leadingColor)
        .filter((color) => color !== null);
}
/**
 * Return the dominant (first) color from a gradient, or the color as-is for
 * solid values. Falls back to the original string if no colors can be extracted.
 */
export function getDominantColor(color) {
    if (!isGradient(color))
        return color;
    return extractColorsFromGradient(color)[0] ?? color;
}
/**
 * Determine the correct CSS properties for a background color.
 * Returns `{ backgroundColor }` for solid colors, `{ background }` for gradients.
 * On React Native, gradients aren't natively supported — use `getDominantColor()`
 * to fall back to a solid color.
 */
export function colorToBackground(color) {
    if (isGradient(color)) {
        return { background: color };
    }
    return { backgroundColor: color };
}
/**
 * Always return a solid hex/rgb color string. Use for properties that don't
 * support gradients: border, box-shadow, accent-color.
 */
export function colorToSolid(color) {
    return getDominantColor(color);
}
/**
 * Return the correct CSS properties for a border color.
 * Solid colors -> `{ borderColor }`, gradients -> `{ borderImage, borderStyle }`.
 */
export function colorToBorder(color) {
    if (isGradient(color)) {
        return {
            borderImage: `${color} 1`,
            borderStyle: "solid",
        };
    }
    return { borderColor: color };
}
